// ============================================================================
// Gráfica de TENDENCIA del score de salud de un número de WhatsApp.
//
// Técnica portada del panel de anunciantes de ForoBeta (Ads/Repository/Order.php
// `buildSvgChart`, decisión Carlos 29-jul-2026: "verifica cómo hicimos unas
// gráficas en forobeta en anunciantes"): SVG inline, cero librerías, área con
// degradado + línea + puntos con <title> nativo (tooltip sin JS, y en móvil el
// dato vive también en la tabla/etiquetas). Igual que allá: rejilla horizontal
// fija, etiquetas X repartidas y un chip ▲/▼ con la variación del periodo.
//
// ✅ ACTIVA desde el 22-ago-2026. Estuvo oculta desde el 29-jul porque con 4 días
// de historia la curva no decía nada; el criterio era ~3 semanas. Al activarla había
// 29 días seguidos (25-jul→22-ago, 10 tenants, sin huecos) y la serie ya cuenta algo:
// el score medio subió de 68 a 80 en once días.
// Para volver a ocultarla: poner esta constante en false y desplegar.
//
// 🚨 DÍAS SIN CONEXIÓN (22-ago-2026, antes de mostrarla al DUEÑO). El semáforo y el
// score NO cuentan lo mismo: waHealth.js:234 fuerza `semaforo='rojo'` cuando la sesión
// no está WORKING, pero el score se sigue calculando aparte y puede quedar en zona
// verde. Medido: Silvestre estuvo en 80 puntos pintado de rojo del 15 al 19-ago, y
// Club Studio lleva desde el 7-ago en 78 (por encima de la raya del 70) con la sesión
// caída. Sin marcarlos, el dueño ve una línea ROJA en zona VERDE contradiciendo el
// chip «En riesgo» de su propia tarjeta. Esos tramos van en GRIS PUNTEADO con su nota.
// ============================================================================

import { useId } from 'react';

// Interruptor único: ponerlo en true muestra la gráfica en todos sus usos.
export const MOSTRAR_TENDENCIA_SALUD = true;

export type PuntoSalud = { dia: string; score: number; semaforo?: string; session_status?: string };
export type HistoriaSalud = {
  puntos: PuntoSalud[];
  dias_con_datos: number;
  delta7: number | null;
  desde: string | null;
};

const COLOR: Record<string, string> = { verde: '#10b981', ambar: '#f59e0b', rojo: '#ef4444' };
const GRIS = '#9ca3af';   // días sin conexión: ni verde ni rojo, «no se pudo medir de verdad»
const dm = (iso: string) => {
  const [, m, d] = iso.split('-');
  return `${Number(d)}/${Number(m)}`;
};

/** Día con la sesión caída. Sin session_status (fotos viejas) se asume conectado. */
const desconectado = (p: PuntoSalud) => !!p.session_status && p.session_status !== 'WORKING';

/** Chip de variación: ▲ +7 · 7d — la lectura que pidió Carlos ("si va mejorando o decayendo"). */
export function ChipTendencia({ delta, dias }: { delta: number | null; dias: number }) {
  if (delta === null || dias < 2) return null;
  const dir = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
  const estilo = {
    up: 'text-emerald-600 bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-300',
    down: 'text-red-600 bg-red-100 dark:bg-red-900/40 dark:text-red-300',
    flat: 'text-slate-500 bg-slate-100 dark:bg-slate-800 dark:text-slate-300',
  }[dir];
  const icono = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '=';
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${estilo}`}>
      {icono} {delta > 0 ? '+' : ''}{delta} · {dias}d
    </span>
  );
}

export default function SaludTendencia({ historia, alto = 120 }: { historia?: HistoriaSalud | null; alto?: number }) {
  // 🚨 El id del degradado DEBE ser único por instancia. Antes se componía con la
  // fecha y el nº de puntos (`grad-salud-2026-08-22-28`) y en el panel admin, donde
  // se pintan varios negocios en la misma página, seis coincidían: SVG resuelve
  // url(#id) por el PRIMERO del documento, así que un negocio en rojo se pintaba con
  // el degradado verde de otro. Detectado al renderizar el QA con datos reales.
  const uid = useId();
  if (!MOSTRAR_TENDENCIA_SALUD) return null;
  const puntos = historia?.puntos ?? [];
  if (puntos.length < 2) {
    return (
      <p className="text-xs text-content-2">
        Aún no hay historia suficiente para la tendencia{historia?.desde ? ` (desde ${dm(historia.desde)})` : ''}.
      </p>
    );
  }

  // Mismo esquema que buildSvgChart: viewBox fijo 1000×alto y padding para ejes.
  const W = 1000, padL = 34, padR = 12, padT = 10, padB = 22;
  const plotW = W - padL - padR, plotH = alto - padT - padB;
  const n = puntos.length;
  const xAt = (i: number) => (n <= 1 ? padL + plotW / 2 : padL + (plotW * i) / (n - 1));
  const yAt = (v: number) => padT + plotH * (1 - Math.max(0, Math.min(100, v)) / 100);  // escala fija 0-100

  const linea = puntos.map((p, i) => `${xAt(i).toFixed(1)},${yAt(p.score).toFixed(1)}`);
  const area = `M${xAt(0).toFixed(1)},${(padT + plotH).toFixed(1)} L${linea.join(' L')} L${xAt(n - 1).toFixed(1)},${(padT + plotH).toFixed(1)} Z`;
  const ultimo = puntos[n - 1];
  const color = COLOR[ultimo.semaforo || ''] || '#6366f1';
  // useId trae dos puntos (":r1:"); se limpian porque el id acaba dentro de un url(#…).
  const gid = `grad-salud-${uid.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const paso = Math.max(1, Math.ceil(n / 6));

  // La línea va por TRAMOS y no en un solo <path>: un tramo que toca un día sin
  // conexión se dibuja gris punteado. Un tramo con un extremo caído tampoco es
  // comparable, por eso basta con que UNO de los dos lo esté.
  const tramos = puntos.slice(1).map((p, i) => ({
    x1: xAt(i), y1: yAt(puntos[i].score), x2: xAt(i + 1), y2: yAt(p.score),
    caido: desconectado(p) || desconectado(puntos[i]),
  }));
  const diasCaidos = puntos.filter(desconectado).length;

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${alto}`} className="w-full h-auto" role="img"
           aria-label={`Tendencia del score de salud: ${puntos.map((p) => `${dm(p.dia)} ${p.score}`).join(', ')}`}>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Umbrales reales del semáforo (70 verde / 40 ámbar): la curva se lee sola */}
        {[70, 40].map((u) => (
          <g key={u}>
            <line x1={padL} y1={yAt(u)} x2={W - padR} y2={yAt(u)}
                  stroke={u === 70 ? '#10b981' : '#f59e0b'} strokeOpacity="0.35" strokeDasharray="5 5" strokeWidth="1.5" />
            <text x={padL - 6} y={yAt(u) + 5} textAnchor="end" fontSize="13" fill="#9ca3af">{u}</text>
          </g>
        ))}

        <path d={area} fill={`url(#${gid})`} />
        {tramos.map((t, i) => (
          <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
                stroke={t.caido ? GRIS : color} strokeWidth="2.5" strokeLinecap="round"
                strokeDasharray={t.caido ? '6 5' : undefined} />
        ))}

        {puntos.map((p, i) => {
          const off = desconectado(p);
          return (
            <circle key={p.dia} cx={xAt(i)} cy={yAt(p.score)} r={i === n - 1 ? 5 : 3.5}
                    fill={off ? GRIS : (COLOR[p.semaforo || ''] || color)}>
              <title>{off
                ? `${p.dia}: ${p.score}/100 · número desconectado (${p.session_status})`
                : `${p.dia}: ${p.score}/100${p.semaforo ? ` (${p.semaforo})` : ''}`}</title>
            </circle>
          );
        })}

        {puntos.map((p, i) => (i % paso === 0 || i === n - 1 ? (
          <text key={`x${p.dia}`} x={xAt(i)} y={alto - 6} textAnchor="middle" fontSize="13" fill="#9ca3af">{dm(p.dia)}</text>
        ) : null))}
      </svg>

      <div className="flex items-center gap-2 text-xs text-content-2 mt-1">
        <ChipTendencia delta={historia?.delta7 ?? null} dias={n} />
        <span>{n} {n === 1 ? 'día medido' : 'días medidos'}</span>
      </div>
      {diasCaidos > 0 && (
        <p className="text-xs text-content-2 mt-1">
          El tramo gris punteado son {diasCaidos} {diasCaidos === 1 ? 'día' : 'días'} con el número
          desconectado: ahí el puntaje se sigue calculando, pero mientras no haya conexión el
          semáforo se marca en rojo aunque el número esté alto.
        </p>
      )}
    </div>
  );
}
